import React from 'react';
import { clockTime } from '../../function-utils/building-time';
import {
  Pill,
  Tag,
} from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import {
  ShiftSummaryResponse,
  IqSection,
  IqStatus,
  STATUS_LABEL,
  DegradeReason,
} from '../../interfaces/iq-interfaces';
import {
  IqCard,
  AuroraHeader,
  Spark,
  CardBody,
  Headline,
  SectionRow,
  RowHead,
  RowText,
  SourceRow,
  SourceChip,
  Caption,
  CountsWrap,
  Band,
  AuroraButton,
  GhostButton,
  SkeletonRow,
} from './iq-shared.styled';

const toneFor = (s: IqStatus): 'success' | 'warning' | 'alert' =>
  s === 'incident' ? 'alert' : s === 'follow_up' ? 'warning' : 'success';

// §6.10 — the server sends a machine reason; the FE owns the words.
const REASON_SENTENCE: Record<DegradeReason, string> = {
  timeout: 'The summary took too long. The full log is below.',
  vendor_error:
    'The summary service is having trouble right now. The full log is below.',
  rate_limited:
    'Too many summaries at once. Try again in a minute — the full log is below.',
  quota_daily:
    'Summaries are unavailable for the rest of today. The full log is below.',
  busy: 'The desk is busy generating summaries. Try again in a moment.',
  invalid_json:
    'The summary came back incomplete, so it was discarded. The full log is below.',
  truncated:
    'The summary came back incomplete, so it was discarded. The full log is below.',
  citation_failed:
    'The summary could not be traced back to the log, so it was discarded. The full log is below.',
  refused:
    'The summary could not be produced for this shift. The full log is below.',
  safety_blocked:
    'Google’s content filter would not summarise this shift. This usually happens when entries contain personal details or describe a serious incident. Nothing has been lost — the full log is below.',
  spii_blocked:
    'Google’s content filter would not summarise this shift. This usually happens when entries contain personal details or describe a serious incident. Nothing has been lost — the full log is below.',
  budget_exceeded:
    'Lobbero IQ has reached this month’s limit for your account.',
  empty: 'Nothing was logged in this window.',
  quiet: 'Quiet shift — the entries are listed below.',
  source_truncated:
    'This window is too large to summarise at once. Narrow the window, or read the full log below.',
  source_moved:
    'The log changed while the summary was being written, so it was discarded. The full log is below.',
  token_map_unreadable:
    'This saved summary could not be fully restored. The full log is below.',
  disabled_globally: '',
  tier_unattested: '',
  capabilities_unverified: '',
  free_tier_block: '',
  tier_canary_free: '',
  model_unverified: '',
};

/* ═══════════════ A REFERENCE IS A LABEL, NEVER A KEY (RC-12) ═══════════════
 *
 * `source_ids` are the tokens the summary was frozen with — 'v:24', 'd:412',
 * 'pa:<uuid>'. When the cited row is on this page the chip says when it
 * happened and what it was; when it is NOT, the prefix is the whole of what is
 * left, so the prefix is what gets named.
 *
 * That gap is a normal path, not a corner case. On the `source_truncated` reply
 * the floor is built from the incident scan, which covers the WHOLE window
 * (read-model.js, INCIDENT_CAP 500), while `entries` carries one page
 * (window.js, maxEntries 120) — so a late incident cites an id this card never
 * received. Printing the token there would end a handover with a database key.
 *
 * The same four words as iq-handoff-card's REF_LABEL, which is private to its
 * own module. */
const REF_LABEL: Record<string, string> = {
  v: 'Visitor entry',
  d: 'Log entry',
  pa: 'Parcel in',
  ph: 'Parcel out',
};

/** An id whose prefix means nothing here — a token shape from a later server,
 *  or prose that reached `source_ids`. "Related record" is true of all of them
 *  and the raw string is true of none of them for a reader. */
const refLabel = (id: string): string => {
  const token = (id || '').trim();
  const cut = token.indexOf(':');

  return (cut > 0 ? REF_LABEL[token.slice(0, cut)] : '') || 'Related record';
};

const timeKind = (data: ShiftSummaryResponse, id: string): string => {
  const e = data.entries.find(x => x.id === id);
  if (!e) return refLabel(id);
  const t = clockTime(e.at, data.window.tz);

  return `${t} · ${e.kind}`;
};

interface Props {
  data: ShiftSummaryResponse | null;
  loading: boolean;
  windowLabel: string;
  entryCount: number | null;
  canGenerate: boolean;
  /** Separates "no building chosen" from "the account is not entitled". The
   *  button is dead in both cases and the reader has to do different things. */
  hasBuilding?: boolean;
  onGenerate: () => void;
  onForce: () => void;
  onRetry: () => void;
  onScrollToSource: (id: string) => void;
}

const Sources: React.FC<{
  ids: string[];
  data: ShiftSummaryResponse;
  onScroll: (id: string) => void;
}> = ({ ids, data, onScroll }) => (
  <SourceRow>
    {ids.map(id => {
      // THE VISIBLE WORDS ARE THE ACCESSIBLE NAME. An aria-label is announced
      // INSTEAD of the chip's text, so a token in it is not a fallback the way
      // `title` is — it is the only thing that reader ever gets. The raw key
      // stays on `title` and nowhere else, the same split iq-handoff-card
      // documents and its tests enforce.
      const label = timeKind(data, id);

      return (
        <SourceChip
          key={id}
          type="button"
          title={id}
          aria-label={`Go to ${label}`}
          data-source-id={id}
          onClick={() => onScroll(id)}
        >
          {label}
        </SourceChip>
      );
    })}
  </SourceRow>
);

const SectionList: React.FC<{
  sections: IqSection[];
  data: ShiftSummaryResponse;
  onScroll: (id: string) => void;
}> = ({ sections, data, onScroll }) => (
  <>
    {sections.map((s, i) => (
      <SectionRow key={`${s.status}-${i}`}>
        <RowHead>
          <Pill tone={toneFor(s.status)}>{STATUS_LABEL[s.status]}</Pill>
          {s.label ? <Tag tone="neutral">{s.label}</Tag> : null}
          {s.system ? <Tag tone="neutral">Recorded automatically</Tag> : null}
        </RowHead>
        {s.text ? <RowText>{s.text}</RowText> : null}
        {s.source_ids.length ? (
          <Sources ids={s.source_ids} data={data} onScroll={onScroll} />
        ) : null}
      </SectionRow>
    ))}
  </>
);

const CountsRow: React.FC<{ data: ShiftSummaryResponse }> = ({ data }) => {
  const c = data.counts;
  if (!c) return null;
  const tags: string[] = [
    `${c.entries} entries`,
    `${c.people_admitted} admitted`,
    `${c.emergency_headcount} emergency`,
    `${c.deliveries_logged} deliveries`,
    `${c.incidents} incidents`,
    `${c.follow_ups} follow-ups`,
  ];

  /* ---- WP-20. Parcels and exits, and the WORDING is load-bearing. ----
   *
   * "on the shelf now" rather than "parcels": packages_pending is an AS-OF
   * figure with no lower bound — it counts everything still waiting whatever day
   * it arrived — and this row sits under a window label, so a bare "8 parcels"
   * would read as eight parcels during this shift.
   *
   * "auto-closed" rather than "departures": an auto-close is the nightly sweep
   * tidying a record nobody closed, not a departure anybody witnessed
   * (chk_vl_exit_machine guarantees those rows carry no recorder). Labelling
   * them as exits would put a number on the screen that means the opposite of
   * what it says.
   *
   * Each is added only when the backend sent it, so an older cached response
   * renders exactly as it did before rather than showing "undefined".
   */
  if (typeof c.packages_logged === 'number') tags.push(`${c.packages_logged} parcels in`);
  if (typeof c.packages_picked_up === 'number')
    tags.push(`${c.packages_picked_up} parcels out`);
  if (typeof c.packages_pending === 'number')
    tags.push(`${c.packages_pending} on the shelf now`);
  if (typeof c.exits_desk_recorded === 'number')
    tags.push(`${c.exits_desk_recorded} exits recorded`);
  if (typeof c.arrivals_still_open === 'number')
    tags.push(`${c.arrivals_still_open} without an exit`);
  if (typeof c.exits_auto_closed === 'number')
    tags.push(`${c.exits_auto_closed} auto-closed`);

  return (
    <CountsWrap>
      {tags.map(t => (
        <Tag key={t} tone="neutral">
          {t}
        </Tag>
      ))}
    </CountsWrap>
  );
};

export const IqSummaryCard: React.FC<Props> = ({
  data,
  loading,
  windowLabel,
  entryCount,
  canGenerate,
  hasBuilding = true,
  onGenerate,
  onForce,
  onRetry,
  onScrollToSource,
}) => {
  const reason = data?.degraded?.reason;
  const safety = reason === 'safety_blocked' || reason === 'spii_blocked';
  // Why the button above is dead. Two causes, and they need different actions
  // from the reader, so they are not collapsed into one sentence.
  const unavailableReason = !hasBuilding
    ? 'Choose a building first — a shift report covers one building\u2019s shift.'
    : 'Lobbero IQ is not switched on for this account yet, so no summary can be '
      + 'written. Everything below is the shift log itself and is unaffected.';
  const errorFamily =
    reason &&
    [
      'timeout',
      'vendor_error',
      'rate_limited',
      'quota_daily',
      'busy',
      'invalid_json',
      'truncated',
      'citation_failed',
      'refused',
      'budget_exceeded',
      'source_moved',
      'token_map_unreadable',
    ].includes(reason);
  const floor = data?.floor_sections || [];

  return (
    <IqCard>
      <AuroraHeader $running={loading}>
        <Spark>✦</Spark>
        Lobbero IQ
        {data?.tier === 'economy' ? <Tag tone="brand">economy</Tag> : null}
        {data?.cached ? <Tag tone="neutral">Cached</Tag> : null}
      </AuroraHeader>

      <CardBody>
        {loading ? (
          <div role="status" aria-live="polite" aria-busy="true">
            <SkeletonRow style={{ width: '90%' }} />
            <div style={{ height: 8 }} />
            <SkeletonRow style={{ width: '70%' }} />
            <div style={{ height: 8 }} />
            <SkeletonRow style={{ width: '80%' }} />
            <Caption style={{ marginTop: 12 }}>
              {entryCount != null
                ? `Reading ${entryCount} entries… Usually 2–4 seconds.`
                : 'Reading the shift… Usually 2–4 seconds.'}
            </Caption>
          </div>
        ) : !data ? (
          <>
            <Caption>{windowLabel}</Caption>
            <div>
              {/* ── "SHIFT REPORT", NOT "HANDOVER" ──
                  These were two different things wearing one word, and the
                  collision cost the owner a real misunderstanding on
                  2026-08-21: "handover is bugged from the iq — there should be
                  a warning that the shift will close."

                  There is no bug. This button calls POST /iq/shift-summary,
                  which READS a window and writes nothing: it can be pressed at
                  any time, re-run, and cached, and it does not touch `shifts`.
                  The thing that closes a shift is POST /shifts/handoff, on the
                  clock card, and it warns before it does ("Your shift ends now
                  and a handover is written…", shift-clock.tsx). Two controls,
                  one named after the other, so pressing the harmless one felt
                  like it should have warned — and pressing it never produced the
                  record the word promised.

                  So the word HANDOVER now belongs to exactly one thing in this
                  product: the frozen record written when a shift ends. What this
                  card makes is a report about a window. */}
              <AuroraButton
                type="button"
                disabled={!canGenerate}
                onClick={onGenerate}
              >
                Generate shift report
              </AuroraButton>
            </div>
            {/* A DISABLED BUTTON WITH NO REASON IS A BROKEN BUTTON.
                This card is now reachable when the account is not entitled — the
                launcher stays visible for an admin, because the reasons IQ is
                off are account-level and they are the only reader who can act on
                one. So the state has to be said, not left for them to infer from
                a control that does nothing. The log below is unaffected either
                way: it is shift data and never went near the model. */}
            {canGenerate ? null : (
              <Caption data-testid="iq-not-entitled">
                {unavailableReason}
              </Caption>
            )}
          </>
        ) : data.summary ? (
          <>
            <RowHead>
              {/* THE CHIP TELLS THE TRUTH ABOUT WHO WROTE THIS.
                  There is always a report now: when the assistant cannot
                  write one — vendor down, safety block, budget spent, already
                  busy — the backend composes it from the rows it retrieved
                  (src/iq/derived-summary.js). That output is arithmetic and
                  quotation, not model prose, and putting the "✦ AI" chip on it
                  would be a claim about its provenance that is simply false. */}
              {data.summary_source === 'derived' ? (
                <Pill tone="neutral">From the log</Pill>
              ) : (
                <Pill tone="brand">✦ AI</Pill>
              )}
              {data.partial ? (
                <Tag tone="warning">Incident added automatically</Tag>
              ) : null}
            </RowHead>
            {data.summary_source === 'derived' ? (
              <Band $tone="warning">
                Lobbero IQ could not write this one
                {reason ? ` (${reason})` : ''}, so it was written from the log
                itself — every line below is a count or a quotation from an
                entry, with nothing inferred.
              </Band>
            ) : null}
            {data.partial ? (
              <Band $tone="warning">
                One incident was added automatically because the AI summary did
                not mention it.
              </Band>
            ) : null}
            <Headline>{data.summary.headline}</Headline>
            <SectionList
              sections={data.summary.sections}
              data={data}
              onScroll={onScrollToSource}
            />
            <CountsRow data={data} />
            <Caption>
              Lobbero cannot say what is still awaiting pickup, and does not
              record who was on duty.
            </Caption>
          </>
        ) : safety ? (
          <>
            <Pill tone="warning">AI summary withheld</Pill>
            <RowText>{REASON_SENTENCE[reason as DegradeReason]}</RowText>
            {floor.length ? (
              <SectionList
                sections={floor}
                data={data}
                onScroll={onScrollToSource}
              />
            ) : null}
            <CountsRow data={data} />
            <div>
              <GhostButton type="button" onClick={onForce}>
                Regenerate anyway
              </GhostButton>
            </div>
          </>
        ) : reason === 'empty' ? (
          <>
            <Pill tone="success">All clear</Pill>
            <RowText>Nothing was logged in this window.</RowText>
            <CountsRow data={data} />
          </>
        ) : reason === 'quiet' ? (
          <>
            <Pill tone="success">Quiet shift</Pill>
            <RowText>{`Quiet shift — ${
              data.counts?.entries ?? ''
            } entries, listed below.`}</RowText>
            {floor.length ? (
              <SectionList
                sections={floor}
                data={data}
                onScroll={onScrollToSource}
              />
            ) : null}
            <CountsRow data={data} />
            <div>
              <GhostButton type="button" onClick={onForce}>
                Summarise anyway
              </GhostButton>
            </div>
          </>
        ) : reason === 'source_truncated' ? (
          <>
            <Pill tone="warning">Window too large to summarise</Pill>
            <RowText>{REASON_SENTENCE.source_truncated}</RowText>
            {floor.length ? (
              <SectionList
                sections={floor}
                data={data}
                onScroll={onScrollToSource}
              />
            ) : null}
            <CountsRow data={data} />
          </>
        ) : (
          <>
            <Pill tone="alert">
              {reason === 'budget_exceeded'
                ? 'Monthly AI limit reached'
                : 'Summary unavailable'}
            </Pill>
            <RowText>
              {errorFamily && reason
                ? REASON_SENTENCE[reason as DegradeReason]
                : 'The summary could not be produced. The full log is below.'}
            </RowText>
            {floor.length ? (
              <SectionList
                sections={floor}
                data={data}
                onScroll={onScrollToSource}
              />
            ) : null}
            <CountsRow data={data} />
            <div>
              <GhostButton type="button" onClick={onRetry}>
                Try again
              </GhostButton>
            </div>
          </>
        )}
      </CardBody>
    </IqCard>
  );
};
